"use server"

import { sdk } from "@lib/config"
import { OptionValueIds } from "@lib/util/product-option-filters"
import { sortProducts } from "@lib/util/sort-products"
import { HttpTypes } from "@medusajs/types"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { getAuthHeaders, getCacheOptions } from "./cookies"
import { getRegion, retrieveRegion } from "./regions"

type ProductListQueryParams = (HttpTypes.FindParams &
  HttpTypes.StoreProductListParams) & {
  options?: string[]
  option_value_id?: string | string[]
}

export const listProducts = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
}: {
  pageParam?: number
  queryParams?: ProductListQueryParams
  countryCode?: string
  regionId?: string
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: ProductListQueryParams
}> => {
  if (!countryCode && !regionId) {
    throw new Error("Country code or region ID is required")
  }

  const limit = queryParams?.limit || 12
  const _pageParam = Math.max(pageParam, 1)
  const offset = _pageParam === 1 ? 0 : (_pageParam - 1) * limit

  let region: HttpTypes.StoreRegion | undefined | null

  if (countryCode) {
    region = await getRegion(countryCode)
  } else {
    region = await retrieveRegion(regionId!)
  }

  if (!region) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("products")),
  }

  return sdk.client
    .fetch<{ products: HttpTypes.StoreProduct[]; count: number }>(
      `/store/products`,
      {
        method: "GET",
        query: {
          limit,
          offset,
          region_id: region?.id,
          fields:
            "*variants.calculated_price,+variants.inventory_quantity,*variants.images,*variants.options,+metadata,+tags,",
          ...queryParams,
        },
        headers,
        next,
        cache: "force-cache",
      }
    )
    .then(({ products, count }) => {
      const nextPage = count > offset + limit ? pageParam + 1 : null

      return {
        response: {
          products,
          count,
        },
        nextPage: nextPage,
        queryParams,
      }
    })
}

export type ProductBySku = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  matched_sku: string
  variant_id: string
  // Calculated price in dollars for the matched variant. Only present when
  // the lookup is made with a region_id.
  price: number | null
  currency_code: string | null
  in_stock: boolean
}

// Resolves variant SKUs (from product.metadata.related) to storefront
// product summaries via the custom /store/products-by-skus endpoint.
// Pass regionId to get per-variant calculated prices and stock status.
export const listProductsBySkus = async (
  skus: string[],
  regionId?: string
): Promise<ProductBySku[]> => {
  if (!skus.length) {
    return []
  }

  const next = {
    ...(await getCacheOptions("products")),
  }

  return sdk.client
    .fetch<{ products: ProductBySku[] }>(`/store/products-by-skus`, {
      method: "GET",
      query: {
        skus: skus.join(","),
        ...(regionId ? { region_id: regionId } : {}),
      },
      next,
      cache: "force-cache",
    })
    .then(({ products }) => products)
    .catch(() => [])
}

export type SystemBuilderShroud = ProductBySku & {
  fan_qty: number
  fan_dia: number
  fans: ProductBySku[]
}

export type SystemBuilderFanConfig = {
  qty: number
  dia: number
  fans: ProductBySku[]
}

export type SystemBuilderRelayPair = {
  standard: ProductBySku | null
  bluetooth: ProductBySku | null
}

export type SystemBuilderData = {
  finish: "black" | "natural"
  core_width: number | null
  inlet_diameter: string | null
  shrouds: SystemBuilderShroud[]
  fans_only: SystemBuilderFanConfig[]
  relays: {
    single: SystemBuilderRelayPair
    dual: SystemBuilderRelayPair
  }
  filters: ProductBySku[]
  tanks: ProductBySku[]
}

// System builder data keyed by radiator finish. Fetched server-side once per
// distinct finish variant so the client can pick the set matching whichever
// variant the shopper actually added to the cart.
export type SystemBuilderByFinish = {
  natural?: SystemBuilderData
  black?: SystemBuilderData
}

// Fetches the guided system-builder payload (finish-matched shrouds, fan
// configs, relays, filters, tanks) for a radiator variant.
export const getSystemBuilder = async (
  productId: string,
  variantId: string,
  regionId: string
): Promise<SystemBuilderData | null> => {
  if (!productId || !variantId || !regionId) {
    return null
  }

  const next = {
    ...(await getCacheOptions("products")),
  }

  return sdk.client
    .fetch<SystemBuilderData>(`/store/system-builder`, {
      method: "GET",
      query: {
        product_id: productId,
        variant_id: variantId,
        region_id: regionId,
      },
      next,
      cache: "force-cache",
    })
    .catch(() => null)
}

// Fetches a single product (with region-calculated variant prices) by
// handle. Used by the Quick View modal to load the full product client-side.
export const getProductByHandle = async (
  handle: string,
  countryCode: string
): Promise<HttpTypes.StoreProduct | null> => {
  if (!handle || !countryCode) {
    return null
  }

  const {
    response: { products },
  } = await listProducts({
    queryParams: { handle, limit: 1 },
    countryCode,
  })

  return products[0] ?? null
}

/**
 * This will fetch 100 products to the Next.js cache and sort them based on the sortBy parameter.
 * It will then return the paginated products based on the page and limit parameters.
 */
export const listProductsWithSort = async ({
  page = 0,
  queryParams,
  sortBy = "created_at",
  countryCode,
  optionValueIds,
}: {
  page?: number
  queryParams?: ProductListQueryParams
  sortBy?: SortOptions
  countryCode: string
  optionValueIds?: OptionValueIds
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: ProductListQueryParams
}> => {
  const limit = queryParams?.limit || 12
  const optionFilters = Array.from(
    new Set((optionValueIds || []).filter(Boolean))
  )

  const {
    response: { products },
  } = await listProducts({
    pageParam: 0,
    queryParams: {
      ...queryParams,
      ...(optionFilters.length ? { option_value_id: optionFilters } : {}),
      limit: 100,
    },
    countryCode,
  })

  const sortedProducts = sortProducts(products, sortBy)

  const pageParam = (page - 1) * limit

  const filteredCount = products.length

  const nextPage = filteredCount > pageParam + limit ? pageParam + limit : null

  const paginatedProducts = sortedProducts.slice(pageParam, pageParam + limit)

  return {
    response: {
      products: paginatedProducts,
      count: filteredCount,
    },
    nextPage,
    queryParams,
  }
}

export type ProductReview = {
  id: string
  reviewer_name: string
  rating: number
  title: string | null
  body: string | null
  verified: boolean
  created_at: string
}

export type ProductReviewList = {
  reviews: ProductReview[]
  count: number
  average: number | null
}

// Approved reviews (plus aggregate stats) for a product from the custom
// /store/reviews endpoint. Short time-based cache so the PDP section, the
// title star summary, and the JSON-LD stats dedupe to a single request.
export const listProductReviews = async (
  productId: string,
  offset = 0,
  limit = 10
): Promise<ProductReviewList> => {
  if (!productId) {
    return { reviews: [], count: 0, average: null }
  }

  return sdk.client
    .fetch<ProductReviewList>(`/store/reviews`, {
      method: "GET",
      query: {
        product_id: productId,
        limit,
        offset,
      },
      next: { revalidate: 120 },
    })
    .then(({ reviews, count, average }) => ({
      reviews: reviews ?? [],
      count: count ?? 0,
      average: average ?? null,
    }))
    .catch(() => ({ reviews: [], count: 0, average: null }))
}

export type OrderReviewProduct = {
  product_id: string
  title: string
  thumbnail: string | null
  reviewed: boolean
}

export type OrderReviewData = {
  display_id: number
  products: OrderReviewProduct[]
}

// Loads the review-form data for an order (which products can be reviewed,
// which already were). Returns null when the token is invalid or expired.
export const getOrderReviewData = async (
  orderId: string,
  token: string
): Promise<OrderReviewData | null> => {
  if (!orderId || !token) {
    return null
  }

  return sdk.client
    .fetch<OrderReviewData>(`/store/reviews/order`, {
      method: "GET",
      query: {
        order_id: orderId,
        token,
      },
      cache: "no-store",
    })
    .catch(() => null)
}

export type SubmitReviewInput = {
  order_id: string
  token: string
  product_id: string
  rating: number
  reviewer_name: string
  title?: string
  body?: string
}

export type SubmitReviewResult =
  | { ok: true }
  | { ok: false; error: string }

// Submits a product review from the tokenized review-form page. Maps the
// backend's error statuses to friendly messages for the form.
export const submitProductReview = async (
  input: SubmitReviewInput
): Promise<SubmitReviewResult> => {
  try {
    await sdk.client.fetch(`/store/reviews`, {
      method: "POST",
      body: input,
      cache: "no-store",
    })
    return { ok: true }
  } catch (e) {
    const status = (e as { status?: number })?.status
    if (status === 409) {
      return { ok: false, error: "You have already reviewed this product." }
    }
    if (status === 401) {
      return { ok: false, error: "This review link is invalid or expired." }
    }
    return { ok: false, error: "Something went wrong. Please try again." }
  }
}

// Subscribes an email address to a back-in-stock notification for a variant
// SKU via the custom /store/notify-restock endpoint. Returns whether the
// subscription was accepted.
export const subscribeRestockNotification = async (
  email: string,
  sku: string
): Promise<boolean> => {
  if (!email || !sku) {
    return false
  }

  return sdk.client
    .fetch<{ subscribed: boolean }>(`/store/notify-restock`, {
      method: "POST",
      body: { email, sku },
      cache: "no-store",
    })
    .then(({ subscribed }) => Boolean(subscribed))
    .catch(() => false)
}
